import { useEffect, useMemo, useState } from "react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { ro } from "date-fns/locale";
import { format } from "date-fns";
import { CheckCircle2, Clock, MapPin, Phone, Mail, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { BRAND } from "@/lib/brand";
import { useReveal } from "@/lib/useReveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function toDateStr(d) {
  // Format as YYYY-MM-DD using local date (avoids UTC shifts)
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function Booking() {
  const r = useReveal();

  const [services, setServices] = useState([]);
  const [serviceId, setServiceId] = useState("");

  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [closed, setClosed] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);

  const [time, setTime] = useState("");

  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  // Load services
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/services`);
        const data = await res.json();
        setServices(data);
        if (data.length && !serviceId) setServiceId(data[0].id);
      } catch (e) {
        toast.error("Nu am putut încărca serviciile. Reîncearcă.");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load availability when date changes
  useEffect(() => {
    if (!date) {
      setSlots([]);
      setClosed(false);
      return;
    }
    const ds = toDateStr(date);
    setLoadingSlots(true);
    setTime("");
    (async () => {
      try {
        const res = await fetch(`${API}/availability?date=${ds}`);
        const data = await res.json();
        setClosed(!!data.closed);
        setSlots(data.slots || []);
      } catch (e) {
        toast.error("Nu am putut încărca intervalele orare.");
      } finally {
        setLoadingSlots(false);
      }
    })();
  }, [date]);

  const selectedService = useMemo(
    () => services.find((s) => s.id === serviceId),
    [services, serviceId]
  );

  // Disable Sundays and past days
  const disabledDays = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return [{ dayOfWeek: [0] }, { before: today }];
  }, []);

  const canSubmit =
    serviceId &&
    date &&
    time &&
    form.name.trim().length >= 2 &&
    form.phone.trim().length >= 6 &&
    /^\S+@\S+\.\S+$/.test(form.email) &&
    !submitting;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/bookings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          phone: form.phone.trim(),
          email: form.email.trim(),
          service_id: serviceId,
          date: toDateStr(date),
          time,
          message: form.message.trim() || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || "Nu am putut crea rezervarea.");
        setSubmitting(false);
        return;
      }
      setConfirmation(data);
      toast.success("Rezervare confirmată!");
    } catch (err) {
      toast.error("Eroare de rețea. Verifică conexiunea.");
    } finally {
      setSubmitting(false);
    }
  }

  function resetFlow() {
    setConfirmation(null);
    setDate(null);
    setTime("");
    setSlots([]);
    setForm({ name: "", phone: "", email: "", message: "" });
  }

  return (
    <section
      id="rezervare"
      data-testid="booking-section"
      className="py-24 sm:py-32 bg-[#F5E6D3]/40"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
        <div ref={r.ref} className={`max-w-3xl mb-14 ${r.className}`}>
          <div className="section-label mb-4">Rezervare</div>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#3B2A22] leading-[1.05]">
            Programează-ți <em className="italic text-[#B88746]">bronzul</em>
          </h2>
          <p className="mt-5 text-[#3B2A22]/70 max-w-2xl">
            Alege serviciul, ziua și intervalul orar disponibil. Confirmarea apare instant
            pe ecran — fără emailuri de așteptat.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-8 lg:gap-10">
          {/* Left: Contact info + Hours */}
          <aside className="lg:col-span-4 space-y-6" data-testid="booking-aside">
            <div className="tbb-card">
              <div className="section-label mb-3">Studio</div>
              <div className="font-serif text-2xl text-[#3B2A22] leading-tight mb-4">
                The Best <em className="italic text-[#B88746]">Bronze</em>
              </div>
              <ul className="space-y-3 text-sm text-[#3B2A22]/80">
                <li className="flex items-start gap-3">
                  <MapPin size={16} className="mt-0.5 text-[#B88746]" />
                  <span>{BRAND.address}</span>
                </li>
                <li className="flex items-start gap-3">
                  <Phone size={16} className="mt-0.5 text-[#B88746]" />
                  <a href={BRAND.phoneHref} className="link-underline" data-testid="booking-phone">
                    {BRAND.phone}
                  </a>
                </li>
                <li className="flex items-start gap-3">
                  <Mail size={16} className="mt-0.5 text-[#B88746]" />
                  <a href={BRAND.emailHref} className="link-underline" data-testid="booking-email">
                    {BRAND.email}
                  </a>
                </li>
              </ul>
            </div>

            <div className="tbb-card">
              <div className="section-label mb-3 flex items-center gap-2">
                <Clock size={14} /> Program
              </div>
              <ul className="space-y-2 text-sm text-[#3B2A22]/85">
                <li className="flex justify-between">
                  <span>Luni – Vineri</span>
                  <span className="font-medium text-[#3B2A22]">09:00 – 19:00</span>
                </li>
                <li className="flex justify-between">
                  <span>Sâmbătă</span>
                  <span className="font-medium text-[#3B2A22]">10:00 – 16:00</span>
                </li>
                <li className="flex justify-between text-[#8A6E59]">
                  <span>Duminică</span>
                  <span>Închis</span>
                </li>
              </ul>
            </div>

            <div className="tbb-card bg-[#3B2A22] text-[#FFFDF9] border-[#3B2A22]">
              <Sparkles size={18} className="text-[#D7B899]" />
              <div className="font-serif text-xl leading-tight mt-3">
                Pregătește pielea cu 24h înainte
              </div>
              <p className="text-sm text-[#FFFDF9]/75 mt-2 leading-relaxed">
                Exfoliere blândă, fără creme hidratante, uleiuri sau parfumuri în ziua
                programării — pentru cel mai uniform finish.
              </p>
            </div>
          </aside>

          {/* Right: Booking flow / Confirmation */}
          <div className="lg:col-span-8">
            {confirmation ? (
              <ConfirmationCard data={confirmation} onReset={resetFlow} />
            ) : (
              <form
                onSubmit={handleSubmit}
                className="tbb-card space-y-8"
                data-testid="booking-form"
              >
                {/* Step 1: Service */}
                <div>
                  <div className="tbb-label">1 · Alege serviciul</div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {services.map((s) => {
                      const active = s.id === serviceId;
                      return (
                        <button
                          key={s.id}
                          type="button"
                          data-testid={`booking-service-${s.id}`}
                          onClick={() => setServiceId(s.id)}
                          className={`text-left rounded-2xl border p-4 transition-all ${
                            active
                              ? "border-[#3B2A22] bg-[#3B2A22] text-[#FFFDF9]"
                              : "border-[#D7B899]/70 bg-[#FFFDF9] hover:border-[#B88746]"
                          }`}
                        >
                          <div className="flex items-center justify-between text-[0.65rem] uppercase tracking-[0.2em]">
                            <span style={{ color: active ? "#D7B899" : "#B88746" }}>
                              {s.duration_min} min
                            </span>
                            <span className={active ? "text-[#FFFDF9]" : "text-[#3B2A22]"}>
                              {s.price} {s.currency}
                            </span>
                          </div>
                          <div className="font-serif text-lg mt-2 leading-tight">
                            {s.name}
                          </div>
                          <p
                            className={`text-xs mt-2 leading-relaxed ${
                              active ? "text-[#FFFDF9]/75" : "text-[#3B2A22]/65"
                            }`}
                          >
                            {s.description}
                          </p>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Step 2: Date + Time */}
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <div className="tbb-label">2 · Alege ziua</div>
                    <div
                      className="rounded-2xl border border-[#D7B899]/70 bg-[#FFFDF9] p-3"
                      data-testid="booking-calendar"
                    >
                      <DayPicker
                        mode="single"
                        selected={date}
                        onSelect={setDate}
                        locale={ro}
                        disabled={disabledDays}
                        weekStartsOn={1}
                        showOutsideDays={false}
                      />
                    </div>
                  </div>

                  <div>
                    <div className="tbb-label">3 · Interval orar</div>
                    <div
                      className="rounded-2xl border border-[#D7B899]/70 bg-[#FFFDF9] p-4 min-h-[260px]"
                      data-testid="booking-slots"
                    >
                      {!date && (
                        <div className="text-sm text-[#8A6E59] italic">
                          Selectează o zi din calendar pentru a vedea intervalele disponibile.
                        </div>
                      )}
                      {date && loadingSlots && (
                        <div className="flex items-center gap-2 text-sm text-[#8A6E59]">
                          <Loader2 size={14} className="animate-spin" /> Se încarcă...
                        </div>
                      )}
                      {date && !loadingSlots && closed && (
                        <div className="text-sm text-[#8A6E59] italic">
                          Studio-ul este închis în această zi. Alege altă dată.
                        </div>
                      )}
                      {date && !loadingSlots && !closed && (
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                          {slots.map((s) => (
                            <button
                              key={s.time}
                              type="button"
                              disabled={!s.available}
                              onClick={() => setTime(s.time)}
                              className={`slot-chip ${time === s.time ? "active" : ""}`}
                              data-testid={`booking-slot-${s.time}`}
                            >
                              {s.time}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Step 3: Personal info */}
                <div>
                  <div className="tbb-label">4 · Datele tale</div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <input
                      type="text"
                      className="tbb-input"
                      placeholder="Nume și prenume"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      data-testid="booking-input-name"
                      required
                    />
                    <input
                      type="tel"
                      className="tbb-input"
                      placeholder="Telefon"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      data-testid="booking-input-phone"
                      required
                    />
                    <input
                      type="email"
                      className="tbb-input md:col-span-2"
                      placeholder="Email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      data-testid="booking-input-email"
                      required
                    />
                    <textarea
                      className="tbb-input md:col-span-2 min-h-[90px] resize-none"
                      placeholder="Mențiuni (opțional) — alergii, preferințe, ocazie specială..."
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      data-testid="booking-input-message"
                    />
                  </div>
                </div>

                {/* Summary + submit */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2 border-t border-[#D7B899]/50">
                  <div className="text-sm text-[#3B2A22]/80" data-testid="booking-summary">
                    {selectedService && (
                      <>
                        <span className="font-medium">{selectedService.name}</span>
                        {date && (
                          <>
                            {" · "}
                            <span>{format(date, "EEEE, d MMMM yyyy", { locale: ro })}</span>
                          </>
                        )}
                        {time && <> · <span className="text-[#B88746] font-semibold">{time}</span></>}
                        {" · "}
                        <span>{selectedService.price} {selectedService.currency}</span>
                      </>
                    )}
                  </div>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={!canSubmit}
                    data-testid="booking-submit"
                  >
                    {submitting ? (
                      <>
                        <Loader2 size={14} className="animate-spin" /> Se trimite...
                      </>
                    ) : (
                      <>Confirmă rezervarea</>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function ConfirmationCard({ data, onReset }) {
  return (
    <div
      className="tbb-card text-center py-12 px-6"
      data-testid="booking-confirmation"
    >
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#B88746]/15 text-[#B88746] mb-5">
        <CheckCircle2 size={32} strokeWidth={1.5} />
      </div>
      <div className="section-label mb-2">Rezervare confirmată</div>
      <h3 className="font-serif text-3xl sm:text-4xl text-[#3B2A22] leading-tight">
        Te așteptăm cu drag, <em className="italic text-[#B88746]">{data.name.split(" ")[0]}</em>
      </h3>
      <p className="mt-4 text-[#3B2A22]/70 max-w-md mx-auto">
        Programarea ta a fost înregistrată. Te rugăm să sosești cu 5 minute mai devreme.
      </p>

      <div className="mt-8 grid sm:grid-cols-2 gap-4 max-w-xl mx-auto text-left">
        <SummaryItem label="Cod rezervare" value={data.code} testId="conf-code" />
        <SummaryItem label="Serviciu" value={data.service_name} testId="conf-service" />
        <SummaryItem label="Data" value={data.date} testId="conf-date" />
        <SummaryItem label="Ora" value={data.time} testId="conf-time" />
        <SummaryItem
          label="Preț"
          value={`${data.service_price} ${data.service_currency}`}
          testId="conf-price"
        />
        <SummaryItem label="Telefon" value={data.phone} testId="conf-phone" />
      </div>

      <button
        type="button"
        onClick={onReset}
        className="btn-secondary mt-10"
        data-testid="booking-reset"
      >
        Nouă rezervare
      </button>
    </div>
  );
}

function SummaryItem({ label, value, testId }) {
  return (
    <div className="rounded-xl bg-[#F5E6D3]/50 border border-[#D7B899]/50 px-4 py-3">
      <div className="text-[0.65rem] uppercase tracking-[0.2em] text-[#8A6E59]">
        {label}
      </div>
      <div className="font-medium text-[#3B2A22] mt-1" data-testid={testId}>
        {value}
      </div>
    </div>
  );
}
